import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useLocation, useNavigate } from "react-router-dom";
import ProviderModeToggle from "./components/ProviderModeToggle";
import IntegrationTabs from "./components/IntegrationTabs";
import OverviewPanel from "./components/OverviewPanel";
import ServiceProvidersPanel from "./components/ServiceProvidersPanel";
import ProviderDetail from "./components/ProviderDetail";
import EditProviderModal from "./components/EditProviderModal";
import { ProviderIntegrationApi } from "../../service/Api/ProviderIntegrationApi";
import {
  DEFAULT_UNMATCHED_CPT_POLICY,
  INTEGRATION_TABS,
  PROVIDER_MODES,
  ProviderModeId,
  ServiceProvider,
  SummaryStat,
  UnmatchedCptPolicyId,
} from "../../utils/integrationConfigData";
import {
  mapProviderListItemToServiceProvider,
  type ProviderListItemApi,
  type ProviderListResponseApi,
} from "./types/providerIntegrationtypes";
import { fetchAuthTypes } from "../../redux/slice/AuthTypeSlice";
import type { AppDispatch, RootState } from "../../redux/store";
import "./IntegrationConfig.scss";

interface IntegrationNavState {
  mode?: ProviderModeId;
  tab?: string;
  provider?: string | null;
}

const INTEGRATION_PATH = "/payer-integration";

const SINGLE_DETAIL_TABS = ["overview", "summary", "crd", "dtr", "pas"];

const isProviderMode = (value: unknown): value is ProviderModeId =>
  value === "multiple" || value === "single";

const normalizeSingleTab = (tab: string | null | undefined) => {
  if (!tab) return "overview";
  if (tab === "summary") return "overview";
  if (SINGLE_DETAIL_TABS.includes(tab)) return tab;
  return "overview";
};

const getValidTab = (mode: ProviderModeId, tab: string | null | undefined) => {
  if (mode === "single") {
    return normalizeSingleTab(tab);
  }

  if (tab && INTEGRATION_TABS.some((item) => item.id === tab)) {
    return tab;
  }
  return "overview";
};

const buildSummaryStats = (
  providers: ServiceProvider[],
  healthyCount = 0,
  unhealthyCount = 0,
): SummaryStat[] => {
  const activeCount = providers.filter((p) =>
    p.status.toLowerCase().includes("active"),
  ).length;
  const errorProvider = providers.find(
    (p) => p.crd === "error" || p.dtr === "error" || p.pas === "error",
  );
  const hasIntegrationError = unhealthyCount > 0 || Boolean(errorProvider);
  const totalCpt = providers.reduce((sum, p) => sum + p.cptCodes, 0);

  return [
    {
      id: "providers",
      title: "Providers",
      badge: { value: String(providers.length), text: "Providers", tone: "success" },
      icon: "fh_three_group",
      iconTone: "success",
      detail: `${activeCount} Active · ${providers.length - activeCount} other`,
    },
    {
      id: "stageHealth",
      title: "Stage Health",
      badge: {
        value: String(hasIntegrationError ? unhealthyCount : healthyCount),
        text: hasIntegrationError ? "Integration Error" : "Healthy",
        tone: hasIntegrationError ? "danger" : "success",
      },
      icon: "fh_check_decagram",
      iconTone: hasIntegrationError ? "danger" : "success",
      detail: errorProvider
        ? `${errorProvider.shortName} · integration error`
        : hasIntegrationError
          ? `${unhealthyCount} unhealthy stage(s)`
          : "All integrations healthy",
    },
    {
      id: "cptRouting",
      title: "CPT Routing",
      badge: { value: "0", text: "Conflicts", tone: "success" },
      icon: "fh_docs_fill",
      iconTone: "success",
      detail: `${totalCpt} Codes · exclusive ownership`,
    },
  ];
};

const IntegrationConfig = () => {
  const dispatch = useDispatch<AppDispatch>();
  const navigate = useNavigate();
  const location = useLocation();
  const navState = (location.state as IntegrationNavState | null) || {};
  const authTypes = useSelector((state: RootState) => state.authType.items);
  const [unmatchedPolicy, setUnmatchedPolicy] = useState<UnmatchedCptPolicyId>(
    DEFAULT_UNMATCHED_CPT_POLICY,
  );
  const [providers, setProviders] = useState<ServiceProvider[]>([]);
  const [healthyCount, setHealthyCount] = useState(0);
  const [unhealthyCount, setUnhealthyCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [editingProviderId, setEditingProviderId] = useState<string | null>(null);
  const didDefaultEmptyTab = useRef(false);

  const providerMode: ProviderModeId = isProviderMode(navState.mode)
    ? navState.mode
    : "multiple";

  const activeTab = getValidTab(providerMode, navState.tab);

  const selectedProviderId =
    navState.provider && providers.some((p) => p.id === navState.provider)
      ? navState.provider
      : providers[0]?.id || null;

  const selectedProvider = useMemo(
    () => providers.find((p) => p.id === selectedProviderId) || providers[0] || null,
    [providers, selectedProviderId],
  );

  const visibleProviders = useMemo(() => {
    if (providerMode === "single") {
      return selectedProvider ? [selectedProvider] : [];
    }
    return providers;
  }, [providerMode, selectedProvider, providers]);

  const summaryStats = useMemo(
    () => buildSummaryStats(providers, healthyCount, unhealthyCount),
    [providers, healthyCount, unhealthyCount],
  );

  const loadProviders = useCallback(async (options?: { silent?: boolean }) => {
    const silent = options?.silent ?? false;
    if (!silent) {
      setLoading(true);
    }
    try {
      const response = (await ProviderIntegrationApi.getProviders()) as
        | ProviderListResponseApi
        | ProviderListItemApi[];
      const list = Array.isArray(response)
        ? response
        : response.providers ?? [];
      const mapped = list.map((dto) =>
        mapProviderListItemToServiceProvider(dto),
      );
      setProviders(mapped);
      if (!Array.isArray(response)) {
        setHealthyCount(response.healthyCount ?? 0);
        setUnhealthyCount(response.unhealthyCount ?? 0);
      } else {
        setHealthyCount(0);
        setUnhealthyCount(0);
      }
    } catch {
      if (!silent) {
        setProviders([]);
        setHealthyCount(0);
        setUnhealthyCount(0);
      }
    } finally {
      if (!silent) {
        setLoading(false);
      }
    }
  }, []);

  const refreshProviders = useCallback(
    () => loadProviders({ silent: true }),
    [loadProviders],
  );

  useEffect(() => {
    dispatch(fetchAuthTypes());
  }, [dispatch]);

  useEffect(() => {
    loadProviders();
  }, [loadProviders]);

  const updateNav = useCallback(
    (
      updates: Partial<IntegrationNavState>,
      options?: { replace?: boolean },
    ) => {
      navigate(INTEGRATION_PATH, {
        replace: options?.replace ?? false,
        state: {
          mode: updates.mode ?? providerMode,
          tab: updates.tab ?? activeTab,
          provider:
            updates.provider !== undefined
              ? updates.provider
              : selectedProviderId,
        },
      });
    },
    [navigate, providerMode, activeTab, selectedProviderId],
  );

  // First empty load only: open Service Providers so Add Provider is visible.
  // Do not force again — otherwise Overview tab clicks cannot stick.
  useEffect(() => {
    if (loading || providerMode !== "multiple" || didDefaultEmptyTab.current) {
      return;
    }
    if (providers.length === 0) {
      didDefaultEmptyTab.current = true;
      if (activeTab !== "providers") {
        updateNav({ tab: "providers" }, { replace: true });
      }
    } else {
      didDefaultEmptyTab.current = true;
    }
  }, [loading, providers.length, providerMode, activeTab, updateNav]);

  const handleProviderSelect = useCallback(
    (provider: ServiceProvider) => {
      if (!provider?.id) return;
      updateNav({ provider: provider.id }, { replace: true });
    },
    [updateNav],
  );

  const handleProviderClick = (provider: ServiceProvider) => {
    updateNav({
      provider: provider.id,
      tab: "providers",
      mode: "multiple",
    });
  };

  const handleProviderEdit = (provider: ServiceProvider) => {
    setEditingProviderId(provider.id);
  };

  const handleProviderEditClose = () => {
    setEditingProviderId(null);
  };

  const handleModeChange = (mode: ProviderModeId) => {
    updateNav({
      mode,
      tab: providers.length === 0 ? "providers" : "overview",
      provider: selectedProviderId || providers[0]?.id || null,
    });
  };

  const handleTabChange = (tabId: string) => {
    updateNav({ tab: tabId });
  };

  const handleSingleDetailTabChange = (tabId: string) => {
    handleTabChange(tabId === "summary" ? "overview" : tabId);
  };

  const handleProviderCreated = (provider: ServiceProvider) => {
    setProviders((prev) => [...prev, provider]);
    updateNav({ provider: provider.id, tab: "providers" });
  };

  const singleDetailTab = activeTab === "overview" ? "summary" : activeTab;
  const totalCptCodes = useMemo(
    () => providers.reduce((sum, p) => sum + (p.cptCodes || 0), 0),
    [providers],
  );

  return (
    <div className="main-content integration-config-page">
      <div className="content-header d-flex justify-content-between align-items-center flex-wrap gap-2">
        <h1 className="page-header mb-0">Integration Configuration</h1>
        <ProviderModeToggle
          modes={PROVIDER_MODES}
          activeMode={providerMode}
          onChange={handleModeChange}
        />
      </div>

      <div className="card ic-main-card">
        <div className="card-body">
          {loading && (
            <div className="ic-inline-message" role="status">
              Loading service providers...
            </div>
          )}
          {!loading && providerMode === "single" ? (
            <div className="ic-single-provider-shell">
              {providers.length === 0 ? (
                <div className="ic-inline-message" role="status">
                  No service providers yet. Switch to Multiple providers to add
                  one.
                </div>
              ) : (
                <ProviderDetail
                  provider={selectedProvider}
                  activeTab={singleDetailTab}
                  onTabChange={handleSingleDetailTabChange}
                  showCptTab={false}
                  unmatchedPolicy={unmatchedPolicy}
                  onUnmatchedPolicyChange={setUnmatchedPolicy}
                  authTypes={authTypes}
                  onProvidersChanged={refreshProviders}
                />
              )}
            </div>
          ) : !loading ? (
            <>
              <IntegrationTabs
                tabs={INTEGRATION_TABS}
                activeTab={activeTab}
                onChange={handleTabChange}
              />

              {activeTab === "overview" ? (
                <OverviewPanel
                  stats={summaryStats}
                  providers={visibleProviders}
                  onProviderClick={handleProviderClick}
                  onProviderEdit={handleProviderEdit}
                />
              ) : (
                <ServiceProvidersPanel
                  providers={visibleProviders}
                  selectedProviderId={selectedProviderId}
                  onProviderSelect={handleProviderSelect}
                  authTypes={authTypes}
                  onProviderCreated={handleProviderCreated}
                  onProvidersChanged={refreshProviders}
                  startInAddMode={providers.length === 0}
                  totalCodes={totalCptCodes}
                />
              )}
            </>
          ) : null}
        </div>
      </div>

      <EditProviderModal
        providerId={editingProviderId}
        show={Boolean(editingProviderId)}
        onClose={handleProviderEditClose}
        onSaved={refreshProviders}
      />
    </div>
  );
};

export default IntegrationConfig;
